import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useBulkPersonalOrderSessionGiftCardStore = defineStore(
  'bulkPersonalOrderSessionGiftCard',
  () => {
    const giftCardForms = ref({})

    // フォーム送信可能かチェックするもの
    const validForm = computed(() => {
      return giftCardSessionToken => {
        const form = giftCardForms.value[giftCardSessionToken]
        if (!form) {
          return false
        }

        const hasUploadedPersonalCardDesignImage =
          form.personalCardDesignFileName !== '' &&
          form.personalCardDesignDataUri !== ''
        const hasCardDesign =
          form.cardDesignId !== '' && form.cardDesignImageUrl !== ''

        const validCardDesign = form.isCardDesign
          ? hasCardDesign
          : form.isAgreementForPersonalCardDesign &&
            hasUploadedPersonalCardDesignImage

        const validTitle = form.title !== ''
        const validItemSkuContents = form.itemSkuContentAttributes.length > 0

        return validCardDesign && validTitle && validItemSkuContents
      }
    })

    const $reset = () => {
      giftCardForms.value = {}
    }

    const setGiftCardForm = ({
      giftCardSessionToken,
      title,
      isCardDesign,
      cardDesignId,
      cardDesignImageUrl,
      personalCardDesignId,
      personalCardDesignFileName,
      personalCardDesignDataUri,
      isAgreementForPersonalCardDesign,
      message,
      senderName,
      publishAmount,
      itemSkuContentAttributes
    }) => {
      giftCardForms.value[giftCardSessionToken] = {
        title: title,
        isCardDesign: isCardDesign,
        cardDesignId: cardDesignId,
        cardDesignImageUrl: cardDesignImageUrl,
        personalCardDesignId: personalCardDesignId,
        personalCardDesignFileName: personalCardDesignFileName,
        personalCardDesignDataUri: personalCardDesignDataUri,
        isAgreementForPersonalCardDesign: isAgreementForPersonalCardDesign,
        message: message,
        senderName: senderName,
        publishAmount: publishAmount,
        itemSkuContentAttributes: itemSkuContentAttributes
      }
    }

    const setTitle = (token, title) => {
      giftCardForms.value[token].title = title
    }

    const setPersonalCardDesign = (
      token,
      personalCardDesignFileName,
      personalCardDesignDataUri
    ) => {
      // ユーザ指定カードデザインが更新されたことがわかるようにidを空にする
      giftCardForms.value[token].personalCardDesignId = ''
      giftCardForms.value[
        token
      ].personalCardDesignFileName = personalCardDesignFileName
      giftCardForms.value[
        token
      ].personalCardDesignDataUri = personalCardDesignDataUri
    }

    const setCardDesign = (token, { cardDesignId, cardDesignImageUrl }) => {
      giftCardForms.value[token].cardDesignId = cardDesignId
      giftCardForms.value[token].cardDesignImageUrl = cardDesignImageUrl
    }

    const setIsCardDesign = (token, isCardDesign) => {
      giftCardForms.value[token].isCardDesign = isCardDesign
    }

    const setIsAgreementForPersonalCardDesign = (
      token,
      isAgreementForPersonalCardDesign
    ) => {
      giftCardForms.value[
        token
      ].isAgreementForPersonalCardDesign = isAgreementForPersonalCardDesign
    }

    const setMessage = (token, message) => {
      giftCardForms.value[token].message = message
    }

    const setSenderName = (token, senderName) => {
      giftCardForms.value[token].senderName = senderName
    }

    const setPublishAmount = (token, publishAmount) => {
      giftCardForms.value[token].publishAmount = publishAmount
    }

    const countUpItemSkuContent = (token, itemSkuId) => {
      const itemSkuContentAttribute = giftCardForms.value[
        token
      ].itemSkuContentAttributes.find(
        itemSkuContentAttribute =>
          itemSkuContentAttribute.itemSkuId == itemSkuId
      )

      const itemCount = parseInt(itemSkuContentAttribute.quantity) + 1
      if (itemCount <= itemSkuContentAttribute.itemSkuQuantityLimit) {
        if (itemCount === itemSkuContentAttribute.itemSkuQuantityLimit) {
          itemSkuContentAttribute.isPlusBtnActive = false
        }
        itemSkuContentAttribute.quantity = itemCount
        itemSkuContentAttribute.isMinusBtnActive = true
      }
    }

    const countDownItemSkuContent = (token, itemSkuId) => {
      const itemSkuContentAttribute = giftCardForms.value[
        token
      ].itemSkuContentAttributes.find(
        itemSkuContentAttribute =>
          itemSkuContentAttribute.itemSkuId == itemSkuId
      )

      const itemCount = parseInt(itemSkuContentAttribute.quantity) - 1
      // ギフト詳細からギフトカード作成画面に遷移した場合に、追加枚数によっては上限枚数以上に表示になる場合がある
      // その場合 [-] ボタンのみ有効で、 [+] ボタンは上限枚数未満になるまで有効にならない
      // @see https://github.com/giftee/kitty/issues/1502
      if (itemCount < itemSkuContentAttribute.itemSkuQuantityLimit) {
        itemSkuContentAttribute.isPlusBtnActive = true
      }
      if (itemCount <= 1) {
        itemSkuContentAttribute.isMinusBtnActive = false
        itemSkuContentAttribute.quantity = 1
      }
      if (1 < itemCount) {
        itemSkuContentAttribute.isMinusBtnActive = true
        itemSkuContentAttribute.quantity = itemCount
      }
    }

    const deleteItemSkuContents = (token, itemSkuId) => {
      const deleteTargetIndex = giftCardForms.value[
        token
      ].itemSkuContentAttributes.findIndex(
        itemSkuContentAttribute =>
          itemSkuContentAttribute.itemSkuId == itemSkuId
      )

      giftCardForms.value[token].itemSkuContentAttributes = giftCardForms.value[
        token
      ].itemSkuContentAttributes.toSpliced(deleteTargetIndex, 1)
    }

    const deleteGiftCard = token => {
      delete giftCardForms.value[token]
    }

    const addItemSkuContent = (token, itemSkuContent) => {
      const targetItemSkuContent = giftCardForms.value[
        token
      ].itemSkuContentAttributes.find(
        itemSkuContentAttribute =>
          itemSkuContentAttribute.itemSkuId == itemSkuContent.itemSkuId
      )

      const targetIndex = giftCardForms.value[
        token
      ].itemSkuContentAttributes.findIndex(
        itemSkuContentAttribute =>
          itemSkuContentAttribute.itemSkuId == itemSkuContent.itemSkuId
      )

      // まだギフトカードに入れていないSKUであれば、シンプルに追加
      if (!targetItemSkuContent) {
        giftCardForms.value[token].itemSkuContentAttributes = [
          ...giftCardForms.value[token].itemSkuContentAttributes,
          itemSkuContent
        ]
        return
      }

      const quantity = targetItemSkuContent.quantity + itemSkuContent.quantity
      // すでにギフトカードにギフトがある場合は、加算したものを置き換える（商品名等が変わった時に逐次反映されるように）
      giftCardForms.value[token].itemSkuContentAttributes = giftCardForms.value[
        token
      ].itemSkuContentAttributes.toSpliced(targetIndex, 1, {
        ...itemSkuContent,
        isPlusBtnActive: isPlusBtnActive(
          quantity,
          itemSkuContent.itemSkuQuantityLimit
        ),
        isMinusBtnActive: isMinusBtnActive(quantity),
        quantity: targetItemSkuContent.quantity + itemSkuContent.quantity
      })
    }

    const isPlusBtnActive = (count, quantityLimit) => count < quantityLimit

    const isMinusBtnActive = count => count > 1

    return {
      giftCardForms,
      validForm,
      $reset,
      setGiftCardForm,
      setTitle,
      setPersonalCardDesign,
      setCardDesign,
      setIsCardDesign,
      setIsAgreementForPersonalCardDesign,
      setMessage,
      setSenderName,
      setPublishAmount,
      deleteItemSkuContents,
      countUpItemSkuContent,
      countDownItemSkuContent,
      deleteGiftCard,
      addItemSkuContent
    }
  },
  {
    persist: true
  }
)
